import React from "react";
import Reveal from "./Reveal";
import ProjectArt from "./ProjectArt";
import { PROJECTS } from "../../content/site";
import { useLanguage } from "../../i18n/LanguageContext";

// Selected work as large typographic index rows. Each row carries a muted
// per-project hue that washes in on hover, its origin following the cursor,
// plus a plate that floats near the cursor on pointer devices and sits inline
// on touch/small screens. The plate shows the project's real artifact (its
// own logo/avatar via `image`), falling back to the stroke-drawn ProjectArt
// emblem for items without one.
// Rows without a public link render as plain rows (no dead anchors).
function ProjectRow({ item, index }) {
  const { t } = useLanguage();
  const Tag = item.href ? "a" : "div";
  const linkProps = item.href
    ? { href: item.href, target: "_blank", rel: "noreferrer noopener" }
    : {};

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    e.currentTarget.style.setProperty(
      "--mx",
      `${((x / rect.width) * 100).toFixed(2)}%`
    );
    // Plate anchor, clamped so the artwork never sits over the medium column
    // or past the row's right edge.
    e.currentTarget.style.setProperty(
      "--plx",
      `${Math.round(Math.min(Math.max(x, rect.width * 0.34), rect.width * 0.8))}px`
    );
  };

  return (
    <Reveal as="li" delay={index * 90}>
      <Tag
        className="dp-project"
        style={{ "--wash": `${item.hue}26`, "--hue": item.hue }}
        onMouseMove={onMouseMove}
        {...linkProps}
      >
        <span className="dp-project__medium">{t(item.medium)}</span>
        <span className="dp-project__plate" aria-hidden="true">
          {item.image ? (
            <img
              className="dp-project__img"
              src={item.image}
              alt=""
              loading="lazy"
              draggable="false"
            />
          ) : (
            <ProjectArt id={item.art} />
          )}
        </span>
        <span className="dp-project__main">
          <span className="dp-project__name">{item.name}</span>
          <span className="dp-project__blurb">{t(item.blurb)}</span>
        </span>
        {item.live && (
          <span className="dp-project__status">{t(PROJECTS.liveLabel)}</span>
        )}
        {item.href && (
          <span className="dp-project__arrow" aria-hidden="true">
            ↗
          </span>
        )}
      </Tag>
    </Reveal>
  );
}

export default function Projects() {
  const { t } = useLanguage();
  const eyebrow = t(PROJECTS.eyebrow);
  return (
    <section id="work" className="dp-section dp-projects" aria-label={eyebrow}>
      <Reveal as="h2" className="dp-eyebrow">
        {eyebrow}
      </Reveal>
      <ul className="dp-projects__list">
        {PROJECTS.items.map((item, i) => (
          <ProjectRow key={item.name} item={item} index={i} />
        ))}
      </ul>
    </section>
  );
}
